import { WithPopover } from '@lightspeed/design-system-react'
import {
  CurrencyDetails,
  PromotionModal,
  PromotionModalOptions,
  TabTypes,
} from '@vend/business-components'
import React, { FunctionComponent, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  useCustomerManager,
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectCurrentSale,
  selectSalePromoCodePromotionId,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { getPromotionById } from '../../../promotions/promotions-resource/promotions-resource'
import { isPromotionDisabled } from '../promotion-functions'
import { AddPromoCodePopover } from './AddPromoCodePopover'
import { PromoCodeLabel } from './PromoCodeLabel'

export const PromoCode: FunctionComponent = () => {
  const [showPopover, setShowPopover] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [
    modalOptions,
    setModalOptions,
  ] = useState<PromotionModalOptions | null>(null)

  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const promoCodePromotionId = useAppSelector(selectSalePromoCodePromotionId)
  const currentSale = useAppSelector(selectCurrentSale)

  const disabledPromotions = isPromotionDisabled(
    saleManager.hasActiveSale(),
    saleManager.getHasActivePromotions(),
    currentSale
  )

  const handlePromotionClick = () => {
    if (disabledPromotions) {
      return
    }

    setShowPopover(true)
  }

  const openPromoModal = async () => {
    if (!promoCodePromotionId) {
      return
    }

    const promotion = await getPromotionById(promoCodePromotionId)
    if (!promotion) {
      return
    }

    const retailer = retailerSettingsManager.get()
    const currencyDetails: CurrencyDetails = {
      culture: retailer.culture,
      code: retailer.currency.code,
      symbol: retailer.currency.symbol,
    }
    const lineItemIds = saleManager.getLineItemProductIds()
    const isTaxExclusive = !registerManager.isTaxInclusive()
    const customerGroups = await customerManager.getCustomerGroups()

    setModalOptions({
      promotion,
      lineItemIds,
      isTaxExclusive,
      totalCustomerGroups: customerGroups ? customerGroups.length : 0,
      defaultTab: TabTypes.PromoCode,
      currencyDetails,
    })

    // Close the popover before opening the modal, so that they don't overlap
    // Also, interacting will the modal will close the popover anyway
    setShowPopover(false)
    setShowModal(true)
  }

  return (
    <>
      {showModal &&
        modalOptions &&
        createPortal(
          <PromotionModal
            close={() => setShowModal(false)}
            options={modalOptions}
          />,
          document.body
        )}

      <WithPopover direction="left">
        <PromoCodeLabel
          disabled={disabledPromotions}
          onClick={handlePromotionClick}
        />

        {showPopover && (
          <AddPromoCodePopover
            closePopover={() => setShowPopover(false)}
            openPromoModal={openPromoModal}
          />
        )}
      </WithPopover>
    </>
  )
}
